import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

import CustomSelect from "@/components/inputs/CustomSelect";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import {
  createNotificationTemplate,
  deleteNotificationTemplate,
  getAdminNotificationRules,
  getNotificationTemplate,
  getNotificationTemplateVariables,
  listNotificationTemplates,
  updateNotificationTemplate,
} from "@/api/notices";

const { Text } = Typography;

// Те же разделы, что и на вкладке «Триггеры» (по trigger_object_type правила).
const FILTER_OPTIONS = [
  { label: "Задачи", value: "task" },
  { label: "Объекты", value: "object" },
  { label: "Раб. группы", value: "unit" },
  { label: "Файлы", value: "file" },
];

const RECIPIENT_ROLE_OPTIONS = [
  { label: "default", value: "default" },
  { label: "author", value: "author" },
  { label: "executor", value: "executor" },
  { label: "coexecutor", value: "coexecutor" },
  { label: "unit_member", value: "unit_member" },
  { label: "observer", value: "observer" },
  { label: "all_participants", value: "all_participants" },
];

const TEXT_FIELDS = [
  { name: "title_template", label: "Заголовок in-app" },
  { name: "content_template", label: "Текст in-app" },
  { name: "email_subject_template", label: "Тема письма" },
  { name: "email_header_template", label: "Шапка письма" },
  { name: "email_body_template", label: "Тело письма" },
  { name: "email_footer_template", label: "Подвал письма" },
];

const codeCellStyle = {
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
};

/**
 * Извлечь имена плейсхолдеров `[объект.атрибут]` из текста. Известные имена —
 * unified_name из каталога переменных. Возвращает массив имён, которых нет в
 * каталоге (подсветка незнакомых; отправка не блокируется).
 */
const findUnknownPlaceholders = (text, knownNames) => {
  if (!text) return [];
  const regex = /\[\s*([^\]]+?)\s*\]/g;
  const seen = new Set();
  let match;
  while ((match = regex.exec(text)) !== null) {
    const name = match[1].trim();
    if (!knownNames.has(name)) seen.add(name);
  }
  return Array.from(seen);
};

const TemplatesTab = () => {
  const [pending, setPending] = useState(true);
  const [search, setSearch] = useState("");
  const [currentFilter, setCurrentFilter] = useState(null);
  const [templates, setTemplates] = useState([]);

  const [rules, setRules] = useState([]);
  const [variables, setVariables] = useState([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPending, setEditorPending] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form] = Form.useForm();
  const [draft, setDraft] = useState({});

  // Отслеживание активного textarea и позиции курсора для вставки переменной.
  const textareaRefs = useRef({});
  const activeFieldRef = useRef({ field: "email_body_template", start: 0, end: 0 });

  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    templates,
    search,
    currentFilter,
  ]);

  // Клиентская фильтрация по разделу (trigger_object_type), как на «Триггерах».
  const tableData = useMemo(() => {
    if (!currentFilter) return templates;
    return templates.filter(
      (t) => t.trigger_object_type === currentFilter
    );
  }, [currentFilter, templates]);

  const knownPlaceholders = useMemo(() => {
    const set = new Set();
    for (const v of variables) {
      if (v.unified_name) set.add(v.unified_name);
    }
    return set;
  }, [variables]);

  // Опции пикера переменных, сгруппированные по объекту.
  const variableGroups = useMemo(() => {
    const byObject = new Map();
    for (const v of variables) {
      if (!v.unified_name) continue;
      const label = v.object_label || v.object_key || "—";
      if (!byObject.has(label)) byObject.set(label, []);
      byObject.get(label).push({
        label: `[${v.unified_name}] — ${v.description || ""}`,
        value: v.unified_name,
      });
    }
    return [...byObject.entries()].map(([label, options]) => ({
      label,
      options,
    }));
  }, [variables]);

  const rememberCaret = (field) => (e) => {
    const el = e.target;
    activeFieldRef.current = {
      field,
      start: el.selectionStart ?? (el.value ? el.value.length : 0),
      end: el.selectionEnd ?? (el.value ? el.value.length : 0),
    };
  };

  // Вставить [unified_name] в позицию курсора последнего активного поля.
  const insertVariable = (unifiedName) => {
    if (!unifiedName) return;
    const { field, start, end } = activeFieldRef.current;
    const snippet = `[${unifiedName}]`;
    const current = form.getFieldValue(field) || "";
    const next =
      current.slice(0, start) + snippet + current.slice(end);
    form.setFieldValue(field, next);
    setDraft((d) => ({ ...d, [field]: next }));

    // Вернуть фокус и поставить курсор после вставленного фрагмента.
    const caret = start + snippet.length;
    activeFieldRef.current = { field, start: caret, end: caret };
    requestAnimationFrame(() => {
      const node = textareaRefs.current[field];
      const dom = node?.resizableTextArea?.textArea;
      if (dom) {
        dom.focus();
        dom.setSelectionRange(caret, caret);
      }
    });
  };

  const fetchTemplates = useCallback(async () => {
    setPending(true);
    try {
      const list = await listNotificationTemplates({ search });
      setTemplates(Array.isArray(list) ? list : []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setPending(false);
    }
  }, [search]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    (async () => {
      try {
        const [rulesList, varsList] = await Promise.all([
          getAdminNotificationRules(),
          getNotificationTemplateVariables(),
        ]);
        setRules(Array.isArray(rulesList) ? rulesList : []);
        setVariables(Array.isArray(varsList) ? varsList : []);
      } catch (error) {
        message.error(error.message);
      }
    })();
  }, []);

  const ruleOptions = useMemo(
    () =>
      rules.map((r) => ({
        label: r.name,
        value: r.uuid,
      })),
    [rules]
  );

  const openCreate = () => {
    setEditingTemplate(null);
    const initial = {
      rule_uuid: undefined,
      recipient_role: "default",
      title_template: "",
      content_template: "",
      email_subject_template: "",
      email_header_template: "",
      email_body_template: "",
      email_footer_template: "",
    };
    form.setFieldsValue(initial);
    setDraft(initial);
    setEditorOpen(true);
  };

  const openEdit = async (row) => {
    setEditorPending(true);
    setEditorOpen(true);
    try {
      const full = await getNotificationTemplate(row.uuid);
      setEditingTemplate(full);
      const initial = {
        rule_uuid: full.rule_uuid,
        recipient_role: full.recipient_role,
        title_template: full.title_template || "",
        content_template: full.content_template || "",
        email_subject_template: full.email_subject_template || "",
        email_header_template: full.email_header_template || "",
        email_body_template: full.email_body_template || "",
        email_footer_template: full.email_footer_template || "",
      };
      form.setFieldsValue(initial);
      setDraft(initial);
    } catch (error) {
      message.error(error.message);
      setEditorOpen(false);
    } finally {
      setEditorPending(false);
    }
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingTemplate(null);
    form.resetFields();
    setDraft({});
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setEditorPending(true);
      if (editingTemplate) {
        const payload = {
          ...values,
          updated_at: editingTemplate.updated_at,
        };
        try {
          await updateNotificationTemplate(editingTemplate.uuid, payload);
          message.success("Шаблон обновлён");
        } catch (error) {
          if (error.status === 409) {
            message.warning(
              "Шаблон изменён другим пользователем, перезагрузите страницу."
            );
          } else {
            message.error(error.message);
          }
          return;
        }
      } else {
        try {
          await createNotificationTemplate(values);
          message.success("Шаблон создан");
        } catch (error) {
          if (error.status === 409) {
            message.error(error.message);
          } else {
            message.error(error.message);
          }
          return;
        }
      }
      closeEditor();
      fetchTemplates();
    } catch (validation) {
      // form validation errors are surfaced by AntD automatically
    } finally {
      setEditorPending(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteNotificationTemplate(row.uuid, row.updated_at);
      message.success("Шаблон удалён");
      fetchTemplates();
    } catch (error) {
      if (error.status === 409) {
        message.warning(
          "Шаблон изменён другим пользователем, перезагрузите страницу."
        );
      } else {
        message.error(error.message);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Правило",
        dataIndex: "rule_name",
        key: "rule_name",
        width: 280,
      },
      {
        title: "Триггер",
        dataIndex: "trigger_event",
        key: "trigger_event",
        width: 240,
        render: (value) => <span style={codeCellStyle}>{value}</span>,
      },
      {
        title: "Роль",
        dataIndex: "recipient_role",
        key: "recipient_role",
        width: 160,
        render: (value) => <span style={codeCellStyle}>{value}</span>,
      },
      {
        title: "Заголовок",
        dataIndex: "title_template",
        key: "title_template",
        ellipsis: true,
      },
      {
        title: "",
        key: "actions",
        width: 100,
        align: "center",
        render: (_, row) => (
          <Space>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            />
            <Popconfirm
              title="Удалить шаблон?"
              description="Действие будет залогировано в audit_logs."
              okText="Удалить"
              okButtonProps={{ danger: true }}
              cancelText="Отмена"
              onConfirm={() => handleDelete(row)}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Flex vertical gap={16} className="h-100">
      <Flex gap={12} align="center" wrap="wrap">
        <Input.Search
          placeholder="Поиск по правилу или роли"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 480 }}
        />
        <CustomSelect
          placeholder="Раздел"
          options={FILTER_OPTIONS}
          style={{ width: 130 }}
          showSearch
          allowClear
          value={currentFilter}
          onChange={setCurrentFilter}
        />
        <Button type="primary" onClick={openCreate}>
          Создать шаблон
        </Button>
      </Flex>

      <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
        <Table
          rowKey="uuid"
          columns={columns}
          dataSource={tableData}
          loading={pending}
          pagination={false}
          scroll={{ x: "100%", y: tableHeight - headerHeight }}
        />
      </div>

      <Modal
        title={editingTemplate ? "Редактирование шаблона" : "Создание шаблона"}
        open={editorOpen}
        onCancel={closeEditor}
        onOk={handleSubmit}
        okText={editingTemplate ? "Сохранить" : "Создать"}
        cancelText="Отмена"
        confirmLoading={editorPending}
        width={780}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(_, all) => setDraft(all)}
        >
          <Form.Item
            label="Правило"
            name="rule_uuid"
            rules={[{ required: true, message: "Выберите правило" }]}
          >
            <Select
              showSearch
              options={ruleOptions}
              optionFilterProp="label"
              disabled={Boolean(editingTemplate)}
              placeholder="Выберите правило"
            />
          </Form.Item>
          <Form.Item
            label="Роль получателя"
            name="recipient_role"
            rules={[{ required: true, message: "Выберите роль" }]}
          >
            <Select
              options={RECIPIENT_ROLE_OPTIONS}
              disabled={Boolean(editingTemplate)}
            />
          </Form.Item>

          <Form.Item label="Вставить переменную" tooltip="Вставляется в позицию курсора последнего активного поля шаблона">
            <Select
              showSearch
              allowClear
              placeholder="Выберите переменную для вставки"
              optionFilterProp="label"
              value={null}
              options={variableGroups}
              onChange={(val) => insertVariable(val)}
            />
          </Form.Item>

          {TEXT_FIELDS.map((field) => (
            <Form.Item key={field.name} label={field.label} name={field.name}>
              <Input.TextArea
                ref={(node) => {
                  textareaRefs.current[field.name] = node;
                }}
                autoSize={{ minRows: 2, maxRows: 8 }}
                onFocus={rememberCaret(field.name)}
                onClick={rememberCaret(field.name)}
                onKeyUp={rememberCaret(field.name)}
                onSelect={rememberCaret(field.name)}
              />
            </Form.Item>
          ))}

          {TEXT_FIELDS.some(
            (f) => findUnknownPlaceholders(draft[f.name], knownPlaceholders).length > 0
          ) && (
            <Flex vertical gap={4} style={{ marginTop: -8 }}>
              <Text type="warning">
                Незнакомые плейсхолдеры (отправка не блокируется, поведение —
                как у текущей системы):
              </Text>
              {TEXT_FIELDS.map((field) => {
                const unknown = findUnknownPlaceholders(
                  draft[field.name],
                  knownPlaceholders
                );
                if (unknown.length === 0) return null;
                return (
                  <span key={field.name} style={codeCellStyle}>
                    {field.label}: {unknown.map((n) => `[${n}]`).join(", ")}
                  </span>
                );
              })}
            </Flex>
          )}
        </Form>
      </Modal>
    </Flex>
  );
};

export default TemplatesTab;
